import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, switchMap, take, tap } from 'rxjs';
import { FlagService } from '../api-users/api/flag.service';
import { FlagResource } from '../api-users/model/flagResource';
import { UserResource } from '../api-users/model/userResource';
import { AuthStingService } from '../auth/auth.service';

@Injectable({
    providedIn: 'root',
})
export class FlagsService {
    private _flag: BehaviorSubject<FlagResource | null> = new BehaviorSubject<FlagResource | null>(null);
    private _flags: BehaviorSubject<FlagResource[] | null> = new BehaviorSubject<FlagResource[] | null>(null);
    loggedUser?: UserResource;

    constructor(
        private readonly _flagDsService: FlagService,
        private readonly _authService: AuthStingService,
    ) {
        this._authService
            .profile()
            .pipe(take(1))
            .subscribe(user => {
                this.loggedUser = user;
            });
    }

    get flag$(): Observable<FlagResource | null> {
        return this._flag.asObservable();
    }

    set flag(value: FlagResource | null) {
        this._flag.next(value);
    }

    get flags$(): Observable<FlagResource[] | null> {
        return this._flags.pipe(
            switchMap(flags => {
                if (!flags) {
                    return this.getFlags();
                }
                return of(flags);
            }),
        );
    }

    public getFlags() {
        return this._flagDsService.getListFlags().pipe(
            tap(flags => {
                this._flags.next((flags as any).data);
            }),
        );
    }

    public switchAllowFlag(id: number, allow: boolean): Observable<FlagResource> {
        return this._flagDsService.updateFlags(id, { allow });
    }

    public addUserToFlag(id: number, userIds: Array<number>): Observable<FlagResource> {
        return this._flagDsService.addUserToFlags(id, { userIds });
    }

    public removeUserFromFlag(id: number, userId: number): Observable<FlagResource> {
        return this._flagDsService.removeUserToFlags(id, userId);
    }

    public updateUserFlagValues(id: number, userId: number, allow: boolean): Observable<FlagResource> {
        return this._flagDsService.updateUserFlags(id, userId, { allow });
    }

    public addCompanyToFlag(id: number, companyIds: Array<number>): Observable<FlagResource> {
        return this._flagDsService.addCompanyToFlags(id, { companyIds } as any);
    }

    public removeCompanyFromFlag(id: number, companyId: number): Observable<FlagResource> {
        return this._flagDsService.removeCompanyFromFlags(id, companyId);
    }

    public updateCompanyFlagValues(id: number, companyId: number, allow: boolean): Observable<FlagResource> {
        return this._flagDsService.updateCompanyFlag(id, companyId, { allow });
    }

    public isEnabled$(flagName: string): Observable<boolean> {
        return this._authService.profile().pipe(
            take(1),
            switchMap(user => {
                user = user.user;
                return this.flags$.pipe(
                    take(1),
                    switchMap(flags => {
                        const flag = flags?.find(f => f.name === flagName);
                        const flagUser = flag?.users?.find(u => u.id === user.id);
                        return of(!!flagUser?.flags?.find(f => f.name.includes(flagName))?.allow || !!flag?.allow);
                    }),
                );
            }),
        );
    }
}
